import { useEffect, useId, useRef, useState } from 'react';
import { Icon } from './Icon';

/**
 * An explanation that is kept off the screen until it is wanted.
 *
 * Some sentences are worth reading once and never again — what an option does, why it is safe.
 * Printed under every control they become furniture that the eye learns to skip, so they are
 * moved behind a mark the reader can ask. The sentence is not deleted and not hidden from
 * assistive technology: the trigger is a real button, focus opens the bubble exactly as hover
 * does, and `aria-describedby` binds the two so a screen reader announces the explanation with
 * the control it belongs to.
 *
 * Clicking only ever opens. A tap on a touch screen raises a pointer-enter and a click in the
 * same gesture, and a trigger that toggled would answer the question and immediately take the
 * answer away again; dismissal is left to Escape, to moving away, or to pressing elsewhere.
 */
export function Tooltip({ label, text }: { readonly label: string; readonly text: string }) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLSpanElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // The sign-in card is not a dialog, but a surface that hosts this one might be, and the
      // innermost thing Escape can close is the thing it should close.
      event.stopPropagation();
      setOpen(false);
    };
    const onPointerDown = (event: PointerEvent) => {
      const node = anchor.current;
      if (node !== null && event.target instanceof Node && !node.contains(event.target)) setOpen(false);
    };

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [open]);

  return (
    <span
      className="tooltip"
      ref={anchor}
      onPointerEnter={() => setOpen(true)}
      onPointerLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="tooltip__trigger"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onClick={() => setOpen(true)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        <Icon name="info" />
      </button>
      {open && (
        <span className="tooltip__bubble" id={id} role="tooltip" dir="auto">
          {text}
        </span>
      )}
    </span>
  );
}
